import { daysRemaining } from 'components/offer/utils';
import { Dot } from 'components/atoms/Dot';
import { HourglassLoader } from 'components/atoms/spinner/HourglassLoader';
import { Icon } from 'components/atoms/Icon.tsx';
import { localize } from 'i18n/localize.ts';
import { OfferImage } from 'components/offer/card/OfferImage';
import { isInactive, OfferStatusAsText } from 'components/offer/utils/status';
import { offerPrice } from 'utils/format.tsx';
import { RotatedRibbon } from 'components/atoms/RotatedRibbon.tsx';
import { type OfferData } from 'service/offer/model';
import { cn } from 'utils/cn';
import './OfferRow.less';
import { OfferRemainingDays } from 'components/offer/OfferRemainingDays';

const $t = localize();

type Props = {
	className?: string;
	offer: OfferData;
	lazyImage?: boolean;
};

export function OfferRow({ className, offer, lazyImage }: Props) {
	const isOfferInactive = isInactive(offer.status);
	const hasVideo = (offer.videos?.length || 0) > 0 || (offer?.videosTotalCount || 0) > 0;
	const isProcessingVideo = offer.videos?.some((video) => video.status === 'processing') ?? false;
	const isReserved = offer.isReserved;

	const days = daysRemaining(offer.validTo ?? '') || 0;
	return (
		<div class={cn('offer-row min-h-20 relative overflow-hidden rounded-2xl flex flex-row', className)}>
			<div
				class={cn(
					isOfferInactive && 'grayscale hover:filter-none',
					'basis-20 h-20 flex-none relative offer-row__image-wrapper rounded-2xl flex items-center justify-center mdp:group-hover/card:rounded-r-none',
				)}
				title={hasVideo && isProcessingVideo ? $t.offer.videoProcessing : undefined}
			>
				{hasVideo && isProcessingVideo ? (
					<HourglassLoader size={20} />
				) : (
					<OfferImage
						alt={offer.name}
						className="size-20 mdp:group-hover/card:rounded-r-none"
						images={offer.images}
						imgClassName="size-20"
						lazy={lazyImage}
					/>
				)}
			</div>
			<div class="mdp:pt-3 px-2 pb-0 flex flex-col">
				<div class="text-red line-clamp-2 leading-5">{offer.name || $t.offer.placeholder.name}</div>
				<div class="mdp:mt-2 line-clamp-1 w-full inline-flex text-dark-blue-50 overflow-visible">
					<b class="text-neutral-black truncate">
						{offer.price !== undefined && !Number.isNaN(offer.price)
							? offerPrice(offer)
							: $t.offer.placeholder.price}
					</b>
					<Dot
						type={
							isReserved
								? 'info'
								: isOfferInactive
									? offer.approvalAdmin === 'rejected'
										? 'error'
										: 'warning'
									: 'success'
						}
						className="relative top-2 ml-2 mr-1.5 hidden mdp:block"
					/>{' '}
					<span class="text-dark-blue hidden mdp:block shrink-0">
						<OfferStatusAsText offer={offer} />
					</span>
					<div class="mdp:hidden flex ml-2 text-dark-blue-50 mdp:group-hover/card:text-dark-blue">
						<Icon type="visibility" fill size={16} className="text-dark-blue-25 mr-1 relative top-0.5" />
						&nbsp;
						<span>{offer.statistics?.detailView || 0}x</span>
					</div>
					<span class="hidden mdp:block">{!isOfferInactive && <OfferRemainingDays days={days} />}</span>
				</div>
				<div class="mdp:hidden line-clamp-1 w-full inline-flex flex-wrap text-dark-blue-50 overflow-visible">
					<Dot
						type={
							isReserved
								? 'info'
								: isOfferInactive
									? offer.approvalAdmin === 'rejected'
										? 'error'
										: 'warning'
									: 'success'
						}
						className="relative top-2 mr-1.5 mdp:hidden"
					/>{' '}
					<span class="text-dark-blue">
						<OfferStatusAsText offer={offer} />
					</span>
					{!isOfferInactive && <OfferRemainingDays days={days} />}
				</div>
			</div>
			{offer.topped && <RotatedRibbon className="top-1 -left-11" label="Top" />}
			{hasVideo && !isProcessingVideo && (
				<div class="absolute top-11 left-11">
					<div class="bg-neutral-35 rounded-full h-8 w-8 text-neutral-white p-1 flex items-center justify-center">
						{isProcessingVideo ? (
							<HourglassLoader size={20} color="white" />
						) : (
							<Icon type="play_circle" size={24} />
						)}
					</div>
				</div>
			)}
		</div>
	);
}
